import styled from "@emotion/styled"
import { PButton } from "@porsche-design-system/components-react"
import { spacingStaticXl } from "@porsche-design-system/components-react/emotion"
import { generateQrCodeRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { trackingActionMap, useBffDynamic } from "src/hooks"
import { ShareVehiclesLinkAndQRCode } from "./components/ShareVehiclesLinkAndQRCode"

const messages = defineMessages({
	heading: {
		defaultMessage: "{value, plural, one {Share Vehicle} other {Share Vehicles}}",
		id: "inventorylist.shareVehicles.heading",
	},
})

interface ShareVehiclesModalContainerProps {
	count: number
	customerFinderUrl: string
}

type Props = ModalProps<ShareVehiclesModalContainerProps>

export const ShareVehiclesModalContainer = withErrorHandler(({ id, modalProps }: Props) => {
	const { count, customerFinderUrl } = modalProps
	const { formatMessage } = useIntl()
	const { hideModal } = useContext(ModalContext)

	const hide = useCallback(() => hideModal(id), [hideModal, id])

	const [qrCodeData, setQrCodeData] = useState<string>()
	const generateQrCode = useBffDynamic(generateQrCodeRoute, "inline")
	useEffect(() => {
		generateQrCode({ body: undefined, pathParams: {}, queryParams: { url: customerFinderUrl } }).then(
			({ dataUrl }) => setQrCodeData(dataUrl),
		)
	}, [customerFinderUrl, generateQrCode])

	const onCopyToClipboard = useCallback(
		() =>
			trackEvent({ action: trackingActionMap.copyShareVehiclesLink, category: "inventory_vehicle_sharing_bar" }),
		[],
	)

	return (
		<Modal id={id} heading={formatMessage(messages.heading, { value: count })}>
			<ShareVehiclesLinkAndQRCode
				qrCodeData={qrCodeData}
				url={customerFinderUrl}
				onCopyToClipboard={onCopyToClipboard}
			/>

			<StyledButtonGroup>
				<PButton type="button" variant="secondary" onClick={hide}>
					<FormattedMessage {...formMessages.close} />
				</PButton>
			</StyledButtonGroup>
		</Modal>
	)
})

const StyledButtonGroup = styled(ButtonGroup)`
	margin-top: ${spacingStaticXl};
`
